<template>
  <section class="qbr-brief-view">
    <section
      v-for="section in sections"
      :key="section.title"
      class="mode-card mode-card--accordion"
    >
      <button
        class="accordion-header"
        type="button"
        :aria-expanded="isSectionExpanded(section.title)"
        @click="toggleSection(section.title)"
      >
        <span class="section-label">{{ section.title }}</span>
        <b-icon
          :icon="isSectionExpanded(section.title) ? 'chevron-down' : 'chevron-right'"
          size="is-small"
          class="accordion-chevron"
        />
      </button>

      <div v-show="isSectionExpanded(section.title)" class="accordion-body">
        <div
          v-for="(entry, entryIndex) in renderableBlocks(section)"
          :key="`${section.title}-block-${entryIndex}`"
          class="accordion-subblock"
        >
          <p v-if="entry.label" class="subblock-label">{{ entry.label }}</p>

          <p
            v-if="entry.block.type === 'text'"
            class="mode-text"
            v-html="linkifyTicketRefs(entry.block.text)"
            @click="onItemClick"
          />

          <ul
            v-else-if="entry.block.type === 'list'"
            class="mode-list"
            :class="isInlineNumbered(entry.block) ? 'mode-list--inline-numbered' : 'mode-list--bulleted'"
          >
            <li
              v-for="(item, index) in entry.block.items"
              :key="`${section.title}-${entryIndex}-${index}`"
              v-html="linkifyTicketRefs(item)"
              @click="onItemClick"
            />
          </ul>

          <dl v-else-if="entry.block.type === 'pairs'" class="mode-pairs">
            <div
              v-for="pair in entry.block.pairs"
              :key="`${section.title}-${entryIndex}-${pair.label}`"
              class="mode-pairs__item"
            >
              <dt>{{ pair.label }}</dt>
              <dd>{{ pair.value }}</dd>
            </div>
          </dl>

          <table v-else-if="entry.block.type === 'table'" class="mode-table">
            <thead>
              <tr>
                <th v-for="column in entry.block.columns" :key="column">{{ column }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in entry.block.rows" :key="`${section.title}-${entryIndex}-row-${rowIndex}`">
                <td v-for="(cell, cellIndex) in row" :key="`${section.title}-${entryIndex}-row-${rowIndex}-${cellIndex}`">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import { ticketRefClass } from '@/utils/visitedTickets'
export default {
  name: 'QbrBriefView',
  props: {
    reportView: {
      type: Object,
      default: () => null,
    },
    signalStrength: {
      type: Object,
      default: () => ({}),
    },
    sections: {
      type: Array,
      default: () => [],
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  data() {
    return {
      expandedSections: {},
    }
  },
  created() {
    const defaultOpen = {}
    for (const section of this.sections) {
      defaultOpen[section.title] = section.defaultOpen === true
    }
    this.expandedSections = defaultOpen
  },
  methods: {
    isSectionExpanded(title) {
      return this.expandedSections[title] === true
    },
    toggleSection(title) {
      this.expandedSections = {
        ...this.expandedSections,
        [title]: !this.isSectionExpanded(title),
      }
    },
    isInlineNumbered(block) {
      const first = String((block?.items || [])[0] || '').trim()
      return /^\d+[.)]/.test(first)
    },
    renderableBlocks(section) {
      const blocks = []
      if (section.block?.hasContent) blocks.push({ label: null, block: section.block })
      for (const group of section.groups || []) {
        if (group.block?.hasContent) blocks.push({ label: group.label, block: group.block })
      }
      return blocks
    },
    linkifyTicketRefs(text) {
      if (!text) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const knownNbrs = new Set(this.ticketRefs.map((t) => String(t.ticketNbr)).filter(Boolean))

      if (knownNbrs.size) {
        const nbrsPattern = [...knownNbrs].map(this.escapeRegex).join('|')
        // Match [T<ticketNbr>] (model-generated bracket+T format) or bare ticketNbr
        const pattern = new RegExp(`\\[T(${nbrsPattern})\\]|\\b(${nbrsPattern})\\b`, 'g')
        const linked = escaped.replace(pattern, (_match, bracketRef, bareRef) => {
          const ticketNbr = bracketRef || bareRef
          return `<span class="${ticketRefClass(ticketNbr)}" data-ticket-nbr="${ticketNbr}" role="button" tabindex="0">${ticketNbr}</span>`
        })
        if (linked !== escaped) return linked
      }

      // Fallback: linkify any #NNNNN pattern the model wrote, even if ticketRefs didn't load
      return escaped.replace(/#(\d{4,10})\b/g, (_match, digits) => {
        return `<span class="${ticketRefClass(digits)}" data-ticket-nbr="${digits}" role="button" tabindex="0">#${digits}</span>`
      })
    },
    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    onItemClick(event) {
      const ticketNbr = event.target?.dataset?.ticketNbr
      if (ticketNbr) {
        this.$emit('ticket-ref-clicked', ticketNbr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.qbr-brief-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
}

.mode-card--accordion {
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: $space-3;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: $space-2;

  &:hover {
    background: $color-surface-alt;
  }
}

.accordion-body {
  padding: 0 $space-3 $space-3;
}

.accordion-chevron {
  flex-shrink: 0;
  color: $color-text-muted;
}

.section-label,
.mode-pairs__item dt {
  margin: 0 0 $space-1;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.accordion-header .section-label {
  margin: 0;
}

.mode-text {
  margin: 0;
  white-space: pre-line;
  line-height: 1.55;
}

.accordion-subblock + .accordion-subblock {
  margin-top: $space-4;
}

.subblock-label {
  margin: 0 0 $space-2;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-muted;
}

.mode-list {
  margin: 0;
  padding-left: 1.2rem;
}

.mode-list--bulleted {
  list-style-type: disc;
}

.mode-list--inline-numbered {
  list-style-type: none;
}

.mode-list li + li {
  margin-top: $space-2;
}

.mode-pairs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: 0;
}

.mode-pairs__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.mode-pairs__item dd {
  margin: 0;
  line-height: 1.45;
}

.mode-table {
  width: 100%;
  border-collapse: collapse;
  font-size: $font-size-sm;
}

.mode-table th,
.mode-table td {
  text-align: left;
  padding: $space-2 $space-3;
  border-bottom: 1px solid $color-border;
}

.mode-table th {
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $color-text-muted;
}

:deep(.ticket-ref) {
  color: $color-primary-dark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }

  &.ticket-ref--visited {
    color: $color-text-muted;
  }
}

@media (max-width: 1100px) {
  .mode-pairs {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .accordion-header {
    padding: $space-3;
  }
}

</style>
